import React from 'react';
import { CheckCircle2, Play, Circle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { useTour } from '../../contexts/TourContext';
import { useAssistant } from '../../contexts/AssistantContext';
import { useApp } from '../../contexts/AppContext';

export const TourMenu: React.FC = () => {
  const { tours, isMenuOpen, setIsMenuOpen, startTour, replayTour, isTourCompleted } = useTour();
  const { setIsOpen: setAssistantOpen } = useAssistant();
  const { currentUser } = useApp();

  const roleTitle = `Tours for ${currentUser.forecasterRole}`;

  return (
    <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl">Product Tours</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{roleTitle}</p>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-3 pb-4">
            {tours.map((tour) => {
              const completed = isTourCompleted(tour.id);
              
              return (
                <Card 
                  key={tour.id} 
                  className={completed ? "border-green-200 bg-green-50/30" : ""}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-base leading-tight">{tour.name}</CardTitle>
                          {completed && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200 shrink-0">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Completed
                            </Badge>
                          )}
                          {!completed && (
                            <Badge variant="outline" className="shrink-0">
                              <Circle className="h-3 w-3 mr-1" />
                              Not Started
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="text-sm">
                          {tour.description}
                        </CardDescription>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {tour.duration}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Button
                      variant={completed ? "outline" : "default"}
                      size="sm"
                      onClick={() => completed ? replayTour(tour.id) : startTour(tour.id)}
                      className="w-full"
                    >
                      {completed ? (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Replay Tour
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Start Tour
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>

        <div className="border-t pt-4 mt-2">
          <p className="text-sm text-muted-foreground text-center">
            Need quick help?{' '}
            <Button
              variant="link"
              className="p-0 h-auto font-semibold text-sm"
              onClick={() => {
                setIsMenuOpen(false);
                setAssistantOpen(true);
              }}
            >
              Open Data Assistant
            </Button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
